import React, { useState } from 'react';
import { Mail, Phone, MessageSquare, Send, HelpCircle, ShieldQuestion } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { sendSupportTicketToTelegram } from '../utils/notifications';

const CustomerSupport = () => {
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        subject: '',
        message: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            // Broadcast to Telegram
            await sendSupportTicketToTelegram({
                subject: formData.subject,
                message: formData.message,
                userEmail: user?.email || 'Unknown User'
            });

            // Set final success state
            setSubmitting(false);
            setSubmitted(true);
            setFormData({ subject: '', message: '' });
        } catch (error) {
            console.error('Support ticket transmission error:', error);
            setSubmitting(false);
            alert('Failed to transmit ticket. Please try again.');
        }
    };

    return (
        <div className="space-y-8 max-w-6xl mx-auto pb-12">
            <header>
                <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                    <HelpCircle className="h-8 w-8 text-orange-500" />
                    Customer Support
                </h1>
                <p className="text-slate-500 font-medium mt-1">Direct support channel for franchise technical and billing operations</p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Contact Cards */}
                <div className="lg:col-span-1 space-y-4">
                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow group">
                        <div className="h-12 w-12 bg-orange-50 rounded-xl flex items-center justify-center text-orange-500 mb-4 group-hover:bg-orange-500 group-hover:text-white transition-colors">
                            <Mail className="h-6 w-6" />
                        </div>
                        <h3 className="font-bold text-slate-800 uppercase tracking-wider text-xs mb-1">Priority Email</h3>
                        <p className="text-lg font-black text-slate-900 tracking-tight mb-2">support@frbillnet.com</p>
                        <p className="text-sm text-slate-500 font-medium">Average response time: <span className="text-emerald-600 font-bold">2 Hours</span></p>
                    </div>

                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow group">
                        <div className="h-12 w-12 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-500 mb-4 group-hover:bg-indigo-500 group-hover:text-white transition-colors">
                            <Phone className="h-6 w-6" />
                        </div>
                        <h3 className="font-bold text-slate-800 uppercase tracking-wider text-xs mb-1">Technical Helpline</h3>
                        <p className="text-lg font-black text-slate-900 tracking-tight mb-2">+91 8007134962</p>
                        <p className="text-sm text-slate-500 font-medium">9 AM - 7 PM (Mon - Sat)</p>
                    </div>

                    <div className="bg-emerald-500 p-6 rounded-2xl shadow-lg shadow-emerald-500/20 text-white group cursor-pointer hover:bg-emerald-600 transition-colors">
                        <div className="h-12 w-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                            <MessageSquare className="h-6 w-6" />
                        </div>
                        <h3 className="font-bold uppercase tracking-wider text-xs mb-1">Instant Chat</h3>
                        <p className="text-xl font-black tracking-tight mb-2">WhatsApp Support</p>
                        <div className="flex items-center gap-2 text-sm font-bold bg-white/10 w-fit px-3 py-1 rounded-full border border-white/20">
                            <div className="h-2 w-2 bg-white rounded-full animate-pulse"></div>
                            Comming Soon
                        </div>
                    </div>
                </div>

                {/* Support Form */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden h-full flex flex-col">
                        <div className="bg-yellow-50 px-8 py-4 border-b border-slate-200 flex items-center gap-2">
                            <ShieldQuestion className="h-5 w-5 text-indigo-600" />
                            <h3 className="font-bold text-slate-800 tracking-tight">Submit Technical Ticket</h3>
                        </div>

                        <div className="p-8 flex-1">
                            {submitted ? (
                                <div className="h-full flex flex-col items-center justify-center text-center space-y-4 animate-in fade-in zoom-in-95 fill-mode-forwards">
                                    <div className="h-20 w-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4">
                                        <Send className="h-10 w-10" />
                                    </div>
                                    <h2 className="text-2xl font-black text-slate-900 tracking-tight">Ticket Submitted Successfully!</h2>
                                    <p className="text-slate-500 font-medium max-w-sm">Our technical team has received your request. We will contact you at your registered email address shortly.</p>
                                    <button
                                        onClick={() => setSubmitted(false)}
                                        className="px-6 py-2 bg-slate-100 text-slate-700 font-bold uppercase tracking-widest text-xs rounded hover:bg-slate-200 transition-colors"
                                    >
                                        Send Another Ticket
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 uppercase tracking-widest mb-2">Issue Subject</label>
                                        <input
                                            required
                                            type="text"
                                            value={formData.subject}
                                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                            placeholder="e.g., Export failure or Invoice printing error"
                                            className="w-full px-4 py-3 bg-yellow-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all font-medium text-slate-900"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 uppercase tracking-widest mb-2">Description</label>
                                        <textarea
                                            required
                                            rows="6"
                                            value={formData.message}
                                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                            placeholder="Please describe the issue in detail..."
                                            className="w-full px-4 py-3 bg-yellow-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none transition-all font-medium text-slate-900 resize-none"
                                        ></textarea>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="w-full flex items-center justify-center gap-3 p-5 bg-indigo-950 hover:bg-black text-white rounded-2xl transition-all shadow-lg shadow-indigo-950/20 group disabled:bg-slate-300 disabled:shadow-none"
                                    >
                                        <span className="font-black text-lg tracking-tight uppercase">
                                            {submitting ? 'Transmitting Ticket...' : 'Dispatch Ticket to Support'}
                                        </span>
                                        <Send className={`h-5 w-5 ${submitting ? 'animate-ping' : 'group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform'}`} />
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerSupport;
